import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface OperatingExpenseBreakdownProps {
  data: {
    researchDevelopment: number;
    salesMarketing: number;
    generalAdministrative: number;
    totalOperatingExpenses: number;
    revenue: number;
    period: string;
  };
}

const formatCurrency = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toFixed(0)}`;
};

const COLORS = {
  'R&D': '#3b82f6',
  'Sales & Marketing': '#14b8a6',
  'G&A': '#f59e0b'
};

export default function OperatingExpenseBreakdown({ data }: OperatingExpenseBreakdownProps) {
  const chartData = [
    { name: 'R&D', value: data.researchDevelopment, color: COLORS['R&D'] },
    { name: 'Sales & Marketing', value: data.salesMarketing, color: COLORS['Sales & Marketing'] },
    { name: 'G&A', value: data.generalAdministrative, color: COLORS['G&A'] }
  ];

  const calculatePercentage = (value: number) => {
    return ((value / data.totalOperatingExpenses) * 100).toFixed(1);
  };

  const calculateRevenuePercentage = (value: number) => {
    return ((value / data.revenue) * 100).toFixed(1);
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-900">Operating Expense Breakdown</h3>
          <span className="text-xs font-medium text-neutral-400 bg-neutral-50 px-2.5 py-1 rounded-full">{data.period}</span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          {/* Total OpEx Header */}
          <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
            <div>
              <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Total Operating Expenses</div>
              <div className="text-xl font-bold text-neutral-900 tabular-nums">{formatCurrency(data.totalOperatingExpenses)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-neutral-500">% of Revenue</div>
              <div className="text-lg font-bold text-neutral-700 tabular-nums">
                {calculateRevenuePercentage(data.totalOperatingExpenses)}%
              </div>
            </div>
          </div>

          {/* Chart + Breakdown side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            {/* Pie Chart */}
            <ResponsiveContainer width="100%" height={240}>
              <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={90}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  strokeWidth={2}
                  stroke="#fff"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [formatCurrency(value), name]}
                  contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                />
                <Legend
                  iconType="circle"
                  iconSize={10}
                  formatter={(value) => <span style={{ fontSize: '12px', color: '#374151' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Detailed Breakdown */}
            <div className="space-y-2">
              {chartData.map((item) => (
                <div key={item.name} className="flex items-center justify-between p-2.5 bg-neutral-50 rounded-lg hover:bg-violet-50/30 transition-colors">
                  <div className="flex items-center gap-2.5">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-sm font-medium text-neutral-800">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-neutral-900 tabular-nums">{formatCurrency(item.value)}</div>
                    <div className="text-[10px] text-neutral-500">
                      {calculatePercentage(item.value)}% of OpEx &middot; {calculateRevenuePercentage(item.value)}% rev
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
